
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FcmProvider } from './fcm.service'
import { UserService } from './user.service'
import { Subject } from 'rxjs';
import { AppService } from './app.service';
import { CounterService } from './counter.service';
import { AnalyticsService } from './analytics.service';
import { NavController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { ChatService } from './chat.service';
import { AppointmentService } from './appointment.service';
import { DatabaseService } from './database.service';
import { ActivityService } from './activity.service';
import { safeFind } from 'src/app/utils';
import { FormService } from './form.service';
import { HelperService, OrganizationService } from '.';
import { Auth, authState, signInWithCustomToken, signInWithEmailAndPassword, User, UserCredential, sendPasswordResetEmail } from '@angular/fire/auth';
import { NativeFirebaseAuthBridgeService } from './native-firebase-auth-bridge.service';

export interface LegalDocumentInfo {
  version: string;
  lastUpdated: string; // ISO date string (YYYY-MM-DD)
  url: string;
}

export interface LegalInfo {
  terms: LegalDocumentInfo;
  privacy: LegalDocumentInfo;
  trainer: LegalDocumentInfo; // optional at global level
}

export interface GlobalLegalSettings {
  legal: LegalInfo;
}

@Injectable()
export class AuthService {


  user: Observable<User>;

  private authStateSubject = new Subject<"login" | "logout" | "authenticated" | "organization-changed">();
  private authData: any;

  constructor(
    private activityService: ActivityService,
    private analyticsService: AnalyticsService,
    private appointmentService: AppointmentService,
    private appService: AppService,
    private chatService: ChatService,
    private counterService: CounterService,
    private dbService: DatabaseService,
    private firebaseAuth: Auth,
    private formService: FormService,
    private fcm: FcmProvider,
    private helperService: HelperService,
    private http: HttpClient,
    private nativeFirebaseAuthBridge: NativeFirebaseAuthBridgeService,
    private navCtrl: NavController,
    private organizationService: OrganizationService,
    private userService: UserService
  ) {
  }

  signup(signup: any) {
    let signupUrl = `${this.appService.getApiURL()}/auth/signup`;
    return this.http.post(signupUrl, signup).toPromise()
      .then(this.helperService.handlePromiseSuccess)
      .catch(this.helperService.handlePromiseRejection)
  }

  registerUser(orgId: string, signup) {
    let signupUrl = `${this.appService.getApiURL()}/organizations/${orgId}/register`;
    return this.http.post(signupUrl, signup).toPromise()
      .then(this.helperService.handlePromiseSuccess)
      .catch(this.helperService.handlePromiseRejection)
  }

  async loadAuthData() {
    this.authData = await this.dbService.getSnapshot("auth");
  }

  getCurrentUser(): Promise<User | null> {
      return new Promise((resolve, reject) => {
        const unsubscribe = authState(this.firebaseAuth).subscribe(user => {
          unsubscribe.unsubscribe();
          resolve(user);
        }, reject);
    });
  }

  async getUid(): Promise<string> {
    const user = this.firebaseAuth.currentUser ?? await this.getCurrentUser();
    if (!user) {
      throw new Error('Not authenticated');
    }
    return user.uid;
  }

  async getToken(): Promise<string | null> {
    const currentUser = this.firebaseAuth.currentUser;
    if(!currentUser) {
      return null;
    }
    return currentUser.getIdToken();
  }

  login(loginData: { email: string, password: string }): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.firebaseAuth, loginData.email, loginData.password)
  }

  async loginWithLandingToken(token: string): Promise<UserCredential> {
    const loginUrl = `${this.appService.getApiURL()}/auth/landing/tokenLogin`;
    const response: any = await this.http.post(loginUrl, { token }).toPromise();
    if (!response) {
      throw new Error("Invalid token login response");
    }

    if (response.customToken && typeof response.customToken === "string") {
      return signInWithCustomToken(this.firebaseAuth, response.customToken);
    }
    throw new Error("Token login response did not include customToken");
  }

  loginAsPromise(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.firebaseAuth, email, password);
  }

  async logout() {
    await this.fcm.removeToken(this.userService.user.uid);
    this.analyticsService.logEvent("logout");
    this.appService.initialized = false;
    this.counterService.clearCounters();
    this.activityService.clearUserActivities();
    this.appointmentService.clearUserAppointments();
    this.chatService.clearUserConversations();
    this.formService.clearUserForms();
    this.userService.clearUserSubscription();
    this.organizationService.clearOrganizationSubscription();
    this.appService.appMode = null;
    await this.nativeFirebaseAuthBridge.signOutNative();
    await this.firebaseAuth.signOut();
    this.analyticsService.clearUser();
    //this.changeLoginStatus(false)
    this.navCtrl.navigateRoot(['/login']);
  }

  forgetPassword(email) {
    return sendPasswordResetEmail(this.firebaseAuth, email);
  }

  setUserPassword(userId: string, password: string) {
     return this.http.post(`${this.appService.getApiURL()}/auth/updatePassword`, {user: userId, password: password}).toPromise();
  }

  publishAuthStateSubject(state: "login" | "logout" | "authenticated" | "organization-changed") {
    this.authStateSubject.next(state);
  }

  getAuthStateSubject(): Subject<"login" | "logout" | "authenticated" | "organization-changed"> {
    return this.authStateSubject;
  }

  isUserAppTester(userId: string) {
    return this.authData?.appTesters?.members?.hasOwnProperty(userId);
  }

  isUserAppAdmin(userId: string) {
    return this.authData?.appAdmin?.members?.hasOwnProperty(userId);
  }

  isUserSuperAdmin(userId: string) {
    return this.authData?.superAdmin?.members?.hasOwnProperty(userId);
  }

  getSuperAdminIds(): string[] {
    const members = this.authData?.superAdmin?.members;
    return members ? Object.keys(members) : [];
  }

  isUserTrainer(): boolean {
    const user = this.userService.getAppUser();
    const orgIds = user?.getOrganizationIds() || [];
    const orgs = this.organizationService.getActiveOrganizations() || [];
    for (const orgId of orgIds) {
      const org = safeFind(orgs, o => o.uid === orgId);
      if(org && org.isUserTrainer(user.uid)) {
        return true
      }
    }

    return false
  }

  isPlatinumTrackingEnabled(): boolean {
    const user = this.userService.getAppUser();
    const orgs = this.organizationService.getActiveOrganizations() || [];
    for (const org of orgs) {
      if(org.plan ==="platinum" && org.status !== 'canceled' && user.organizations[org.uid].status === "active") {
        return true
      }
    }

    return false
  }

  activeUserHasTrackingEnabled(): boolean {
    const user = this.userService.getAppUser();
    if(user?.trackingEnabled()) {
      return true
    }

    return this.isUserTrainer() || this.isPlatinumTrackingEnabled();
  }

  getLegalInfo(): Promise<LegalInfo> {
     return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/auth/legalInfo`)
      .toPromise()
      .then(res => { resolve(<LegalInfo>res) })
      .catch(err => { reject(err) })
    });
  }
}
